import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { N0, B300 } from "@atlaskit/theme/colors";

import { useLocalizedHref } from "@/libraries/web-framework/src/localization/use-localized-href";
import { CollectionOrderKey } from "@/templates/software/confluence/templates/confluence-templates-page/src/config/collection-order.ts";

// Utility function to sanitize strings for use as HTML IDs
const sanitizeId = (str: string): string => {
    return str
        .toLowerCase()
        .replace(/[^a-z0-9-]/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "");
};

const rootCss = css({
    "borderRadius": 6,
    "background": N0,
    "boxShadow":
        "0px 0px 1px 0px rgba(9, 30, 66, 0.31), 0px 1px 1px 0px rgba(9, 30, 66, 0.25)",
    "width": "368px",
    "@media (max-width: 639.99px)": {
        width: 352
    }
});

const contentCss = css({
    display: "flex",
    flexDirection: "column"
});

const imageContainerCss = css({
    height: 215
});

const textContainerCss = css({
    display: "flex",
    flexDirection: "column",
    gap: 8,
    padding: 24
});

const titleCss = css({
    fontSize: 20,
    fontFamily: "Charlie Display",
    fontWeight: "700",
    margin: 0
});

const textLinkCss = css({
    color: B300,
    fontSize: 16,
    fontFamily: "Charlie Text"
});

export interface CollectionCardProps {
    urlSlug: CollectionOrderKey;
    name: string;
    templateCount: number;
}

const imageUrls: Record<CollectionOrderKey, string> = {
    "project-management":
        "https://wac-cdn.atlassian.com/misc-assets/webp-images/project-management.svg",
    "strategic-planning":
        "https://wac-cdn.atlassian.com/misc-assets/webp-images/strategic-planning.svg",
    "product-managers":
        "https://wac-cdn.atlassian.com/misc-assets/webp-images/product-managers.svg",
    "hr": "https://wac-cdn.atlassian.com/misc-assets/webp-images/hr.svg",
    "knowledge-base":
        "https://wac-cdn.atlassian.com/misc-assets/webp-images/knowledge-base.svg",
    "remote-work":
        "https://wac-cdn.atlassian.com/misc-assets/webp-images/remote-work.svg",
    "software-development":
        "https://wac-cdn.atlassian.com/misc-assets/webp-images/software-development.svg",
    "product-launch":
        "https://wac-cdn.atlassian.com/misc-assets/webp-images/product-launch.svg",
    "designs-at-scale":
        "https://wac-cdn.atlassian.com/misc-assets/webp-images/designs-at-scale.svg",
    "marketing-strategy":
        "https://wac-cdn.atlassian.com/misc-assets/webp-images/marketing-strategy.svg",
    "employee-development":
        "https://wac-cdn.atlassian.com/misc-assets/webp-images/employee-development.svg"
};

export const CollectionCard = (props: CollectionCardProps) => {
    const localizedLink = useLocalizedHref(
        `/software/confluence/templates/collections/${props.urlSlug}`
    );

    const imageUrl = imageUrls[props.urlSlug];

    const uniquePartnerTemplateId = sanitizeId(props.urlSlug || "");
    // Generate unique IDs for accessibility
    const headingId = `collection-heading-${uniquePartnerTemplateId}`;
    const linkId = `collection-link-${uniquePartnerTemplateId}`;

    return (
        <div css={rootCss} data-testid="confluence-templates-collection-card">
            <a
                href={localizedLink}
                id={linkId}
                aria-labelledby={`${linkId} ${headingId}`}
                // todo: analytics
            >
                <div css={contentCss}>
                    <div css={imageContainerCss}>
                        {/* eslint-disable-next-line no-restricted-syntax */}
                        <img src={imageUrl} alt="" />
                    </div>

                    <div css={textContainerCss}>
                        <h3 css={titleCss} id={headingId}>
                            {props.name}
                        </h3>
                        <span
                            css={textLinkCss}
                            className="collection-card-text-link"
                        >
                            <FormattedMessage
                                id="confluence.templates.collection-card.view-link"
                                defaultMessage={`View {count} templates`}
                                values={{ count: props.templateCount }}
                            />
                            {" →"}
                        </span>
                    </div>
                </div>
            </a>
        </div>
    );
};
